import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    MenuItem,
    Select
} from '@mui/material';
import './statistics.css';
import {
    countAllUsersByTenant,
    countConnectedUsersByTenant,
    countConnectedGuestByTenant,
    tenantsMap,
    CountAllUsersByTenantByMap,
    StatAllConnectedByTenantByMap,
    StatAllCommunicationByTenantByMap,
    ActiveAccounts,
    getTenantMonthlyReport
} from './store/statisticsThunks'
import {
    setSuccessMessage,
    setError
} from './store/statisticsSlice'


import { useTranslation } from 'react-i18next';

import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis,
    CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// Fonction pour formater le label de la date/heure en utilisant fr-FR
const formatDateLabel = (timestamp) => {
    // Vérifier si la valeur est valide et peut être convertie en date
    if (!timestamp) return '';

    const date = new Date(timestamp);

    // Vérifier si la date est valide
    if (isNaN(date)) return String(timestamp); // Retourne la chaîne originale si ce n'est pas une date

    const options = {
        // Dépend de la durée (LAST_HOUR, DAY, etc.)
        // Pour une granularité horaire/journalière, on peut utiliser :
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    };

    // Si on veut une granularité plus large (semaine/mois) :
    // if (selectedDuration !== 'LAST_HOUR' && selectedDuration !== 'DAY') {
    //     options.day = '2-digit';
    //     options.month = 'short';
    //     delete options.hour;
    //     delete options.minute;
    // }

    // Utilisation de 'fr-FR' pour le format français
    return date.toLocaleString('fr-FR', options);
};


export default function Statistics() {
    const dispatch = useDispatch();
    const { t } = useTranslation();


    const {
        successMessage,
        usersCount,
        tenantsMapList,
        countAllUsersByTenantByMap,
        mergedPoints,
        communicationCount,
        activeAccounts,
        error
    } = useSelector((state) => state.statistics);

    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedDuration, setSelectedDuration] = useState('LAST_HOUR');
    // ... reste du code (useEffect pour le dispatch initial, options, handleChange, etc.)

    useEffect(() => {
        // Dispatching individually is fine for unrelated counts
        dispatch(countAllUsersByTenant());
        dispatch(countConnectedUsersByTenant());
        dispatch(countConnectedGuestByTenant());
        dispatch(tenantsMap());
    }, [dispatch]);

    const options = tenantsMapList?.flatMap((tenant) =>
        tenant.maps.map((map) => ({
            label: `${tenant.name} - ${map?.map_tenant?.name}`,
            tenantId: +tenant.id,
            mapId: +map.id,
        }))
    ) || [];

    const handleChange = (e) => {
        const index = e.target.value;
        const option = options[index];
        setSelectedOption(option);
        countUsersByTenantByMap(option?.tenantId, option?.mapId);
        dispatch(ActiveAccounts({ body: { map_id: option?.mapId, tenantId: option?.tenantId } }));

        dispatch(
            StatAllConnectedByTenantByMap({
                period: selectedDuration,
                map_id: option?.mapId,
                tenantId: option?.tenantId
            })
        );

        dispatch(
            StatAllCommunicationByTenantByMap({
                period: selectedDuration,
                map_id: option?.mapId,
                tenantId: option?.tenantId
            })
        );
    };

    useEffect(() => {
        if (options.length > 0 && !selectedOption) {
            const firstOption = options[0];
            setSelectedOption(firstOption);
            if (firstOption.mapId && firstOption.tenantId) {
                countUsersByTenantByMap(firstOption?.tenantId, firstOption?.mapId);

                dispatch(
                    StatAllConnectedByTenantByMap({
                        period: 'LAST_HOUR',
                        map_id: firstOption?.mapId,
                        tenantId: firstOption?.tenantId
                    })
                );

                dispatch(
                    StatAllCommunicationByTenantByMap({
                        period: 'LAST_HOUR',
                        map_id: firstOption?.mapId,
                        tenantId: firstOption?.tenantId
                    })
                );


                dispatch(ActiveAccounts({ body: { map_id: firstOption?.mapId, tenantId: firstOption?.tenantId } }));
            }
        }
    }, [options, selectedOption, dispatch]);


    const countUsersByTenantByMap = async (tenantId, mapId) => {
        try {
            dispatch(CountAllUsersByTenantByMap({ params: [{ mapId: mapId, tenantId: tenantId }] }));
        } catch (error) {
            console.error("Erreur :", error);
        }
    };

    const handleChangePeriode = (e) => {
        const duration = e.target.value;
        setSelectedDuration(duration)
        statUserConnectedByTenantByMap(duration);
    };

    const statUserConnectedByTenantByMap = async (duration) => {
        try {

            dispatch(
                StatAllConnectedByTenantByMap({
                    period: duration,
                    map_id: selectedOption?.mapId,
                    tenantId: selectedOption?.tenantId
                })
            );
            dispatch(
                StatAllCommunicationByTenantByMap({
                    period: duration,
                    map_id: selectedOption?.mapId,
                    tenantId: selectedOption?.tenantId
                })
            );
        } catch (error) {
            console.error("Erreur :", error);
        }
    };

    const durationList = [
        { id: 'LAST_HOUR', label: 'Dernière heure' },
        { id: 'DAY', label: 'Jourée' },
        { id: 'LAST_3_DAYS', label: '3 dernier jours' },
        { id: 'ROLLING_WEEK', label: 'Semaine glissante (7 jours)' },
        { id: 'FIXED_WEEK', label: 'Semaine fixe' },
        { id: 'MONTH', label: 'Mois' },
    ];


    // Ajoutez cette fonction dans votre composant ou à l'extérieur si vous voulez la rendre plus générique
    const getTickFormatter = (duration) => {
        let options = {};
        switch (duration) {
            case 'LAST_HOUR':
            case 'DAY':
                options = { hour: '2-digit', minute: '2-digit', hour12: false };
                break;
            case 'LAST_3_DAYS':
            case 'ROLLING_WEEK':
            case 'FIXED_WEEK':
                options = { day: '2-digit', month: 'short', hour: '2-digit', hour12: false };
                break;
            case 'MONTH':
                options = { day: '2-digit', month: 'short' };
                break;
            default:
                options = { day: '2-digit', month: 'short' };
                break;
        }

        return (tick) => {
            if (!tick) return '';
            const date = new Date(tick);
            if (isNaN(date)) return String(tick);
            return date.toLocaleString('fr-FR', options);
        };
    };


    return (
        <>
            <div className='stat-container'>
                <div className='container'>
                    <div className='title-stat'>
                        <div className='logo-title'>
                            <div className='title-stat-logo'>
                                <img className="people_image" src="/people_map.svg" alt="people map" />
                            </div>
                            <h3>{t("stat_space")} </h3>
                        </div>
                        <Button
                            className='export_monthly_report'
                           onClick={() => dispatch(getTenantMonthlyReport( 11, 2025))}
                        >
                            {t("export_monthly_report")}
                        </Button>
                        <Select
                            onChange={handleChange}
                            value={
                                selectedOption
                                    ? options.findIndex(
                                        (o) =>
                                            o?.tenantId === selectedOption?.tenantId &&
                                            o?.mapId === selectedOption?.mapId
                                    )
                                    : ""
                            }
                            sx={{
                                width: "15.625rem",
                                height: "15%",
                                borderRadius: "0.5rem",
                                marginLeft: "auto",
                                color: "#697586",
                                "& .MuiSelect-select": {
                                    display: "flex",
                                    alignItems: "center",
                                    minHeight: "unset",
                                    padding: "0.5rem",
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    minHeight: "15%",
                                },
                            }}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 190,
                                        overflowY: "auto",
                                    },
                                },
                            }}
                        >
                            {options.map((option, index) => (
                                <MenuItem key={`${option?.tenantId}-${option?.mapId}`} value={index}>
                                    {t(option?.label)}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>

                    <div className='header-stat'>
                        <div className='info-users-box'>
                            <h4>
                                {t("total_user_limit")}
                            </h4>
                            <span className='info-users-value'>{activeAccounts} {t("active_accounts")}{/* <span>/ {usersCount?.nombreMaximal} {t("maximum")}</span> */}</span>
                        </div>
                        <div className='info-users-box'>
                            <h4>
                                {t("number_of_connected_users")}
                            </h4>
                            <span className='info-users-value'>{countAllUsersByTenantByMap?.onlineUsers} {t("users")} <span>{t("real_time")}</span></span>
                        </div>
                        <div className='info-users-box'>
                            <h4>
                                {t("number_of_connected_guests")}
                            </h4>
                            <span className='info-users-value'>{countAllUsersByTenantByMap?.onlineGuests} {t("active_guests")}<span></span></span>
                        </div>
                    </div>

                    <div className='body-stat'>
                        <div className='title-box'>
                            <h4>Statistique d'usage </h4>


                            <Select
                                onChange={handleChangePeriode}
                                value={selectedDuration}
                                sx={{
                                    width: "15.625rem",
                                    height: "15%",
                                    borderRadius: "0.5rem",
                                    marginLeft: "auto",
                                    color: "#697586",
                                    "& .MuiSelect-select": {
                                        display: "flex",
                                        alignItems: "center",
                                        minHeight: "unset",
                                        padding: "0.5rem",
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        minHeight: "15%",
                                    },
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 190,
                                            overflowY: "auto",
                                        },
                                    },
                                }}
                            >
                                {durationList.map((option, index) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {t(option.label)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>
                        <div>
                            <div className="chart-container">
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={mergedPoints || []}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        {/* Utilisation de tickFormatter ici */}
                                        <XAxis dataKey="label" tickFormatter={getTickFormatter(selectedDuration)} />
                                        <YAxis />
                                        <Tooltip labelFormatter={getTickFormatter(selectedDuration)} />
                                        <Legend />
                                        <Bar dataKey="countUser" fill="#3b82f6" name={t("users")} />
                                        <Bar dataKey="countGuest" fill="#00b05c" name={t("guests")} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="chart-container" style={{ marginTop: '2.5rem' }}>
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart data={communicationCount || []}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        {/* Utilisation de tickFormatter ici */}
                                        <XAxis dataKey="label" tickFormatter={getTickFormatter(selectedDuration)} />
                                        <YAxis />
                                        <Tooltip labelFormatter={getTickFormatter(selectedDuration)} />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="countInvitation"
                                            stroke="#3b82f6"
                                            name="Invitations"
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="countMessage"
                                            stroke="#10b981"
                                            name="Messages"
                                            strokeDasharray="5 5"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                    </div>
                    <pre>{console.log(tenantsMapList) || null}</pre>
                </div>
            </div>
        </>)
}